import { cloneElement, useState } from 'react'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'

interface Props {
  // The plain-text value to write to the clipboard. Trimmed before writing
  // so the BDR never gets stray whitespace artifacts from the surrounding
  // HTML — that's the bug the button is fixing (RADAR-SE-004).
  value: string | null | undefined
  // Optional accessible label / tooltip. Falls back to "Copy <value>".
  label?: string
  className?: string
}

// Tiny inline "copy this field" button. Click → write to clipboard, flip
// the icon to a checkmark for ~1.2s. No-op when value is empty.
export default function CopyButton({ value, label, className }: Props) {
  const [copied, setCopied] = useState(false)
  const clean = (value || '').trim()

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!clean) return
    const writer = navigator.clipboard?.writeText?.bind(navigator.clipboard)
    if (!writer) return
    writer(clean).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      },
      () => { /* permission denied / insecure context — silently no-op */ },
    )
  }

  if (!clean) return null

  // Size the SVG explicitly (width/height attrs on the element) instead of
  // relying on a CSS variant — the icons in Ico.tsx don't carry size props,
  // so without this they'd render at the browser's default SVG viewport and
  // overflow the 18px button.
  const iconEl = copied ? Ico.check : Ico.copy
  const icon = cloneElement(iconEl, { width: 11, height: 11 })

  return (
    <button
      type="button"
      className={cls(
        'inline-flex items-center justify-center w-[18px] h-[18px] p-0 ml-1 align-middle',
        'bg-transparent border border-transparent rounded-[2px] cursor-pointer',
        'transition-[color,background,border-color] duration-[120ms] ease-in-out',
        'shrink-0',
        copied
          ? 'text-moss bg-moss/10 border-moss/30'
          : 'text-ink-300 hover:text-ink-1000 hover:bg-ink-100 hover:border-ink-200',
        className,
      )}
      onClick={onClick}
      title={copied ? 'Kopierat!' : (label || `Kopiera "${clean}"`)}
      aria-label={label || `Kopiera ${clean}`}
    >
      {icon}
    </button>
  )
}
